/**
 * A type-ahead picker.
 *
 * Native `<datalist>` cannot carry a sublabel or a "create new" escape hatch,
 * and a full combobox library is more surface than this needs, so: an input, a
 * filtered listbox, arrow keys, Enter, Escape. §9 requires keyboard navigation
 * and a label, and this is the shape that gets both without a dependency.
 *
 * **The create row is not gated on having typed something**, and that is a
 * correction. It used to render only when `term.trim() !== ''`, which is
 * inverted: the moment a user most needs "create" is when the list holds
 * nothing useful — which is exactly when they have not typed. `AccountPicker`
 * pre-seeds its list by searching for `"a"`, so the field opens looking like a
 * complete set of choices and the one state that offered create was the state
 * you had to go out of your way to reach. It is a dead end, and the Meetings
 * account picker had no other door out of it.
 *
 * The row still names the typed term when there is one, because
 * `Create account "Acme"` says more than `Create account` does; with nothing
 * typed it is just the label, and the dialog opens with an empty name.
 *
 * **The `Enter` path carries the same rule.** It has its own copy of the
 * condition, and a fix to one and not the other leaves the keyboard
 * disagreeing with the mouse about what the component offers. Both branches
 * sit inside `onCreate !== undefined`, so a picker passing no `onCreate` — the
 * quick log's, which sets `allowCreate={false}` — renders and reaches nothing.
 *
 * The row is deliberately **still shown on an exact match**: §6.8 is
 * warn-with-candidates and never block, `CreateAccountDialog` already runs
 * that check, and hiding the row would be this component quietly making a
 * blocking decision the spec forbids.
 */

import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Check, ChevronsUpDown, Plus } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

export interface Option {
  value: string;
  label: string;
  sublabel?: string | undefined;
}

export interface ComboboxProps {
  value: string | null;
  onChange: (value: string | null) => void;
  options: Option[];
  placeholder?: string;
  /** Called as the user types, for a server-backed search. */
  onSearch?: (term: string) => void;
  /** Renders an inline "create <term>" row — the escape hatch on the account picker. */
  onCreate?: (term: string) => void;
  createLabel?: string;
  disabled?: boolean;
  id?: string;
  emptyText?: string;
}

export function Combobox({
  value,
  onChange,
  options,
  placeholder = 'Search…',
  onSearch,
  onCreate,
  createLabel = 'Create',
  disabled = false,
  id,
  emptyText = 'No matches',
}: ComboboxProps): React.ReactElement {
  const [term, setTerm] = useState('');
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const listId = useId();

  const selected = useMemo(
    () => options.find((option) => option.value === value) ?? null,
    [options, value],
  );

  const filtered = useMemo(() => {
    if (term.trim() === '') return options.slice(0, 50);
    const needle = term.toLowerCase();
    return options
      .filter(
        (option) =>
          option.label.toLowerCase().includes(needle) ||
          (option.sublabel ?? '').toLowerCase().includes(needle),
      )
      .slice(0, 50);
  }, [options, term]);

  useEffect(() => {
    if (!open) return undefined;
    const onDocumentClick = (event: MouseEvent): void => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocumentClick);
    return () => document.removeEventListener('mousedown', onDocumentClick);
  }, [open]);

  const commit = (option: Option): void => {
    onChange(option.value);
    setTerm('');
    setOpen(false);
  };

  return (
    <div className="relative" ref={containerRef}>
      <div className="relative">
        <Input
          id={id}
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          autoComplete="off"
          disabled={disabled}
          value={open ? term : (selected?.label ?? '')}
          placeholder={selected === null ? placeholder : selected.label}
          onFocus={() => setOpen(true)}
          onChange={(event) => {
            setTerm(event.target.value);
            setOpen(true);
            setHighlight(0);
            onSearch?.(event.target.value);
          }}
          onKeyDown={(event) => {
            if (event.key === 'ArrowDown') {
              event.preventDefault();
              setOpen(true);
              setHighlight((index) => Math.min(index + 1, filtered.length - 1));
            } else if (event.key === 'ArrowUp') {
              event.preventDefault();
              setHighlight((index) => Math.max(index - 1, 0));
            } else if (event.key === 'Enter') {
              const option = filtered[highlight];
              if (option !== undefined) {
                event.preventDefault();
                commit(option);
              } else if (onCreate !== undefined) {
                // No `term.trim() !== ''` here, for the same reason as the
                // rendered row below: the two must agree, or the keyboard
                // reaches an affordance the mouse cannot, or the reverse.
                event.preventDefault();
                onCreate(term.trim());
                setOpen(false);
              }
            } else if (event.key === 'Escape') {
              setOpen(false);
            }
          }}
          className="pr-8"
        />
        <ChevronsUpDown
          className="pointer-events-none absolute right-2 top-2.5 h-4 w-4 text-muted-foreground"
          aria-hidden="true"
        />
      </div>

      {open && (
        <ul
          id={listId}
          role="listbox"
          className="absolute z-30 mt-1 max-h-64 w-full overflow-y-auto rounded-md border bg-card py-1 shadow-lg"
        >
          {filtered.length === 0 && onCreate === undefined && (
            <li className="px-3 py-2 text-xs text-muted-foreground">{emptyText}</li>
          )}
          {filtered.map((option, index) => (
            <li key={option.value}>
              <button
                type="button"
                role="option"
                aria-selected={option.value === value}
                onMouseEnter={() => setHighlight(index)}
                onClick={() => commit(option)}
                className={cn(
                  'flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-sm',
                  index === highlight && 'bg-accent',
                )}
              >
                <span className="min-w-0">
                  <span className="block truncate">{option.label}</span>
                  {option.sublabel !== undefined && (
                    <span className="block truncate text-[11px] text-muted-foreground">
                      {option.sublabel}
                    </span>
                  )}
                </span>
                {option.value === value && <Check className="h-3.5 w-3.5" aria-hidden="true" />}
              </button>
            </li>
          ))}
          {onCreate !== undefined && (
            <li className="border-t">
              <button
                type="button"
                onClick={() => {
                  onCreate(term.trim());
                  setOpen(false);
                }}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-primary"
              >
                <Plus className="h-3.5 w-3.5" aria-hidden="true" />
                {createLabel}
                {term.trim() !== '' && <> &ldquo;{term.trim()}&rdquo;</>}
              </button>
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
